import React from 'react';
import {
  Alert,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

const formatWon = (value) => `${Math.round(Number(value) || 0).toLocaleString('ko-KR')}원`;

const NumberCell = ({ value, onChange, suffix, width = 90 }) => (
  <TableCell align="right">
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
      <TextField
        type="number"
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{ width }}
        inputProps={{ style: { textAlign: 'right' } }}
      />
      {suffix && <Typography variant="caption" color="text.secondary">{suffix}</Typography>}
    </Stack>
  </TableCell>
);

/**
 * 3단계: 원가 산출 - 부품별 라인(소재비+가공비, 구매품은 단가×수량만) + 조립비
 * + 외주 항목을 합쳐 합계를 낸다. 재질 단가/가공속도는 부품마다 다를 수 있어
 * 2단계(해석 확인) 표/수정 패널에서 고치고, 여기서는 "시간당 가공비"(공용)와
 * 부품별 "셋업"만 고친다 - 과제 요구("시간당 가공비는 global, 셋업은 부품별")대로.
 */
const CadQuoteCostStep = ({
  form, costInputs, computed,
  onCostInputChange, onPartSetupChange,
  onOutsourceChange, onAddOutsource, onRemoveOutsource,
  settingMeta, onRequestSavePrices,
}) => {
  const partsById = Object.fromEntries((form.parts || []).map((p) => [p.id, p]));
  // 한 번도 저장 안 했으면(UPD_DT가 NULL) "임시 기본값" - 요구사항 그대로.
  const priceSourceText = settingMeta?.updDt
    ? `회사 단가표 기준(마지막 수정: ${settingMeta.updNm || '알수없음'} / ${settingMeta.updDt})`
    : '임시 기본값 — 2단계에서 부품별로 수정하거나 아래에서 저장해 회사 단가표로 만드세요.';

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" gutterBottom>3. 원가 산출</Typography>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Alert severity="info" sx={{ flex: 1, minWidth: 240 }}>{priceSourceText}</Alert>
        <Button size="small" variant="outlined" onClick={onRequestSavePrices}>
          이 단가를 회사 단가표로 저장
        </Button>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>부품</TableCell>
            <TableCell align="right">수량</TableCell>
            <TableCell align="right">소재비</TableCell>
            <TableCell align="right">가공비</TableCell>
            <TableCell align="right">구매비</TableCell>
            <TableCell align="right">셋업(분)</TableCell>
            <TableCell align="right">소계</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {computed.lines.map((line) => {
            const part = partsById[line.partId];
            if (!part) return null;
            return (
              <TableRow key={line.partId}>
                <TableCell>{part.name}{part.purchased ? ' (구매품)' : ''}</TableCell>
                <TableCell align="right">{line.quantity}</TableCell>
                <TableCell align="right">{formatWon(line.materialCost)}</TableCell>
                <TableCell align="right">{formatWon(line.machiningCost)}</TableCell>
                <TableCell align="right">{formatWon(line.purchaseCost)}</TableCell>
                <TableCell align="right">
                  {part.purchased ? '-' : (
                    <TextField
                      type="number"
                      size="small"
                      value={part.setupMinutes}
                      onChange={(e) => onPartSetupChange(part.id, Number(e.target.value))}
                      sx={{ width: 70 }}
                      inputProps={{ style: { textAlign: 'right' } }}
                    />
                  )}
                </TableCell>
                <TableCell align="right">{formatWon(line.subtotal)}</TableCell>
              </TableRow>
            );
          })}

          <TableRow>
            <TableCell colSpan={7}>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">조립비(선택) - 부품을 하나로 조립하는 시간</Typography>
              </Stack>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={2}>
              <Typography variant="caption" color="text.secondary">조립 시간 × 시간당 가공비</Typography>
            </TableCell>
            <NumberCell value={costInputs.assemblyMinutes} onChange={(v) => onCostInputChange('assemblyMinutes', v)} suffix="분" width={80} />
            <TableCell align="right" colSpan={3}>{formatWon(computed.assemblyLaborCost)}</TableCell>
            <TableCell />
          </TableRow>
          <TableRow>
            <TableCell colSpan={2}>
              <Typography variant="caption" color="text.secondary">시간당 가공비(공용)</Typography>
            </TableCell>
            <NumberCell value={costInputs.hourlyRateWon} onChange={(v) => onCostInputChange('hourlyRateWon', v)} suffix="원/시간" width={100} />
            <TableCell align="right" colSpan={3} />
            <TableCell />
          </TableRow>

          <TableRow>
            <TableCell colSpan={7}>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">외주 항목(방전·열처리·표면처리 등)</Typography>
                <IconButton size="small" onClick={onAddOutsource} title="항목 추가">
                  <AddIcon fontSize="small" />
                </IconButton>
              </Stack>
            </TableCell>
          </TableRow>
          {costInputs.outsourceItems.map((item) => (
            <TableRow key={item.id}>
              <TableCell colSpan={4}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="예: 방전가공"
                  value={item.name}
                  onChange={(e) => onOutsourceChange(item.id, 'name', e.target.value)}
                />
              </TableCell>
              <NumberCell value={item.amount} onChange={(v) => onOutsourceChange(item.id, 'amount', v)} suffix="원" width={110} />
              <TableCell align="right" colSpan={2}>
                <IconButton size="small" onClick={() => onRemoveOutsource(item.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {costInputs.outsourceItems.length === 0 && (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography variant="body2" color="text.secondary">외주 항목 없음</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Divider sx={{ my: 2 }} />

      <Stack spacing={1} sx={{ maxWidth: 360, ml: 'auto' }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2">소재비 + 가공비 + 구매비 + 외주</Typography>
          <Typography variant="body2">{formatWon(computed.subtotal)}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">마진율</Typography>
            <TextField
              type="number"
              size="small"
              value={Math.round(costInputs.marginRate * 100)}
              onChange={(e) => onCostInputChange('marginRate', Number(e.target.value) / 100)}
              sx={{ width: 70 }}
              inputProps={{ style: { textAlign: 'right' } }}
            />
            <Typography variant="body2">%</Typography>
          </Stack>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{formatWon(computed.supplyPrice)}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2">부가세(10%)</Typography>
          <Typography variant="body2">{formatWon(computed.vat)}</Typography>
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>합계</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{formatWon(computed.total)}</Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default CadQuoteCostStep;
